import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { LambdaClient, InvokeCommand } from "@aws-sdk/client-lambda";
import { Button } from "@mui/material";
import { useLoading } from "../../../context/LoadingContext";
import config from "../../../aws-exports";
import { getParameterFromManager } from "../../../services/Utils";
import DataTable from "../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../Requirement/components/common/SearchFilter";
import AlertModal from "../../Requirement/components/common/AlerModel";
import {
  fetchData,
  postQuery,
} from "../../Requirement/services/RequirementServices";
import "../../../styles/general.css";
import "../../../styles/amtpl.css";
// Modal state for alerts
import dayjs from "dayjs";
// Utility function to format date from "2025-09-30 00:00:00" to "09/30/25"
const formatDateToMMDDYY = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString.replace(" ", "T"));
  if (isNaN(date)) return "";
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
};

// Utility function to update pricing calculations based on GPMargin
const updatePricingBasedOnMargin = (selectedOffer, newMargin) => {
  if (!selectedOffer || !selectedOffer.BasePrice) {
    return selectedOffer;
  }

  const basePrice = selectedOffer.BasePrice || 0;
  const quantity = selectedOffer.Quantity || 1;
  const exportDutyPercentage = selectedOffer.ExportDutyPercentage || 0;
  const shippingInsurancePercentage =
    selectedOffer.ShippingInsurancePercentage || 0;

  // Calculate new FOB Sale Price
  const newFOBSalePrice = basePrice + newMargin;

  // Calculate Export Duty based on FOB Sale Price
  const newExportDuty = (newFOBSalePrice * exportDutyPercentage) / 100;

  // Calculate Shipping & Insurance based on quantity
  const newShippingInsurance = shippingInsurancePercentage * quantity;

  // Calculate new CIF Sale Price
  const newCIFSalePrice =
    newFOBSalePrice +
    newExportDuty +
    newShippingInsurance +
    (selectedOffer.GST || 0);

  // Return updated offer object
  return {
    ...selectedOffer,
    GPMargin: newMargin,
    FOBSalePrice: newFOBSalePrice,
    ExportDuty: newExportDuty,
    ShippingInsurance: newShippingInsurance,
    CIFSalePrice: newCIFSalePrice,
    PricePerMT: newFOBSalePrice / quantity,
    TotalPrice: newFOBSalePrice,
  };
};

const GTPOffer = () => {
  const { setLoading, loading } = useLoading();

  // Products and filters
  const [products, setProducts] = useState([]);
  const [countries, setCountries] = useState([]);

  // Active tab state
  const [activeTab, setActiveTab] = useState("offerInformation");

  // VM Offers state
  const [vmOffersFilters, setVmOffersFilters] = useState({
    product: "",
    grade: "",
    createdDate: "",
    quantity: "",
    destinationCountry: "",
  });
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    autoClose: false,
    isConfirm: false,
    onConfirm: null,
  });

  // Comment modal state for rejection
  const [commentModal, setCommentModal] = useState({
    show: false,
    comment: "",
  });

  // Handle modal close
  const handleModalClose = () => {
    setModal((prev) => ({ ...prev, show: false }));
  };

  // Handle comment modal close
  const handleCommentModalClose = () => {
    setCommentModal({ show: false, comment: "" });
  };

  // Handle comment input change
  const handleCommentChange = (event) => {
    setCommentModal((prev) => ({ ...prev, comment: event.target.value }));
  };

  // Search fields configuration for SearchFilter component
  const fields = [
    {
      name: "product",
      label: "Product",
      type: "dropdown",
      options: [
        { value: "", label: "Select a product" },
        ...[...new Set(products.map((product) => product.ProductName))].map(
          (product) => ({
            value: product,
            label: product,
          })
        ),
      ],
      value: vmOffersFilters.product,
      onChange: (value) => {
        setVmOffersFilters((prev) => ({
          ...prev,
          product: value,
          grade: "", // Reset grade when product changes
        }));
      },
    },
    {
      name: "grade",
      label: "Grade",
      type: "dropdown",
      options: [
        { value: "", label: "Select a Grade" },
        ...products
          .filter((product) => product.ProductName === vmOffersFilters.product)
          .map((product) => ({
            value: product.Grade,
            label: product.Grade,
          })),
      ],
      value: vmOffersFilters.grade,
      onChange: (value) => {
        setVmOffersFilters((prev) => ({ ...prev, grade: value }));
      },
      disabled: !vmOffersFilters.product,
    },
    {
      name: "createdDate",
      label: "Created Date",
      type: "date",
      value: vmOffersFilters.createdDate,
      onChange: (value) => {
        setVmOffersFilters((prev) => ({ ...prev, createdDate: value }));
      },
    },
    {
      name: "quantity",
      label: "Quantity (MT)",
      type: "dropdown",
      options: [
        { value: "", label: "Select Range" },
        { value: "50-100", label: "50-100 MT" },
        { value: "100-200", label: "100-200 MT" },
        { value: "200-500", label: "200-500 MT" },
        { value: "Above-500", label: "Above 500 MT" },
      ],
      value: vmOffersFilters.quantity,
      onChange: (value) => {
        setVmOffersFilters((prev) => ({ ...prev, quantity: value }));
      },
    },
    {
      name: "destinationCountry",
      label: "Destination Country",
      type: "dropdown",
      options: [
        { value: "", label: "Select Country" },
        ...countries.map((country) => ({
          value: country.Value,
          label: country.Value,
        })),
      ],
      value: vmOffersFilters.destinationCountry,
      onChange: (value) => {
        setVmOffersFilters((prev) => ({ ...prev, destinationCountry: value }));
      },
    },
  ];

  // GP Offers state
  const [gpOffers, setGPOffers] = useState([]);
  const [selectedGPOffer, setSelectedGPOffer] = useState(null);
  const [currentGPOfferPage, setCurrentGPOfferPage] = useState(1);
  const [totalGPOfferPages, setTotalGPOfferPages] = useState(1);

  // GP Offer Seller Aggregate state
  const [gpOfferSellerAggregateItems, setGPOfferSellerAggregateItems] =
    useState([]);
  const [
    selectedGPOfferSellerAggregateItem,
    setSelectedGPOfferSellerAggregateItem,
  ] = useState(null);
  const [
    currentGPOfferSellerAggregatePage,
    setCurrentGPOfferSellerAggregatePage,
  ] = useState(1);
  const [
    totalGPOfferSellerAggregatePages,
    setTotalGPOfferSellerAggregatePages,
  ] = useState(1);

  // Pricing calculations
  const [sellersQuantity, setSellersQuantity] = useState(0);
  const [sellersPrice, setSellersPrice] = useState(0);
  const [sellersPriceForQuantity, setSellersPriceForQuantity] = useState(0);
  const [INRToUsd] = useState(0.012);

  // Margin update state
  const [isMarginModified, setIsMarginModified] = useState(false);
  const [originalMargin, setOriginalMargin] = useState(null);

  // Constants
  const pageSize = 5;

  // Define table columns for DataTable
  const columns = [
    {
      id: "OfferNumber",
      numeric: false,
      label: "Offer Number",
      renderCell: (row) => row.OfferNumber || "N/A",
    },
    // {
    //   id: "CreatedAt",
    //   numeric: false,
    //   label: "Created",
    //   renderCell: (row) => formatDateToMMDDYY(row.CreatedAt),
    // },
    // {
    //   id: "Expires",
    //   numeric: false,
    //   label: "Expires",
    //   renderCell: (row) => formatDateToMMDDYY(row.Expires),
    // },
    {
      id: "CreatedAt",
      numeric: false,
      label: "Created On",
      //renderCell: (row) => formatDateToMMDDYY(row.CreatedAt),
      renderCell: (row) =>
        row.CreatedAt
          ? dayjs(row.CreatedAt).format("DD/MM/YYYY")
          : "",
    },
    {
      id: "Expires",
      numeric: false,
      label: "Expires On",
      renderCell: (row) =>
        row.Expires
          ? dayjs(row.Expires).format("DD/MM/YYYY")
          : "",
    },
    {
      id: "ProductName",
      numeric: false,
      label: "Product",
      renderCell: (row) => row.ProductName || "N/A",
    },
    {
      id: "Grade",
      numeric: false,
      label: "Grade",
      renderCell: (row) => row.Grade || "N/A",
    },
    {
      id: "Quantity",
      numeric: false,
      label: "Quantity",
      renderCell: (row) => formatData(row.Quantity),
    },
    {
      id: "PricePerMT",
      numeric: false,
      label: "Price",
      renderCell: (row) => formatData(row.PricePerMT),
    },
    {
      id: "DestinationCountry",
      numeric: false,
      label: "Destination",
      renderCell: (row) => row.DestinationCountry || "N/A",
    },

    {
      id: "OfferType",
      numeric: false,
      label: "Offer Type",
      renderCell: (row) => row.OfferType || "N/A",
    },
    {
      id: "GPOOfferStatus",
      numeric: false,
      label: "Offer Status",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";

        switch (row.GPOOfferStatus) {
          case "BAFO":
            bgColor = "#e0f2fe";
            textColor = "#0369a1";
            break;
          case "Firmoffer":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          default:
            bgColor = "#fef3c7";
            textColor = "#b45309";
            row.GPOOfferStatus = "Indicative"; // Set default status
            break;
        }

        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {row.GPOOfferStatus}
          </span>
        );
      },
    },
    {
      id: "CreatedBy",
      numeric: false,
      label: "Created By",
      renderCell: (row) => row.CreatedBy || "N/A",
    },
  ];

  // Utility function to format data
  function formatData(data) {
    return data?.toLocaleString();
  }

  // Get backend name for Lambda function
  const getBackendName = async () => {
    return await getParameterFromManager("backend");
  };

  // Fetch GP Offers
  const fetchGPOffers = useCallback(
    async (customParams = {}) => {
      setLoading(true);
      try {
        const body = {
          //target: "listGPOfferAdminDetails",
          target: "listGPOfferDetailsByUserType",
          userOrder: "CreatedAt DESC, OfferSequenceID DESC",
          rowStart:
            customParams.rowStart || (currentGPOfferPage - 1) * pageSize,
          pageSize: pageSize,
          ...customParams,
        };
        body.filterTerms = body.filterTerms || {};
        body.filterTerms.AdminStatus = 0;
        body.filterTerms.OfferUserType = "GlobalPartner";

        const result = await fetchData(body);

        if (result && (result.items || result.statusCode === 200)) {
          const offers = result.items || [];
          setGPOffers(offers);
          setTotalGPOfferPages(
            Math.ceil((result.total || offers.length) / pageSize)
          );
        } else {
          console.error("Error fetching gpOffers: ", result);
          setGPOffers([]);
          setTotalGPOfferPages(1);
        }
      } catch (error) {
        console.error("Error fetching gpOffers: ", error);
        setGPOffers([]);
        setTotalGPOfferPages(1);
      } finally {
        setLoading(false);
      }
    },
    [currentGPOfferPage, setLoading]
  );

  // Fetch GP Offer Seller Aggregates
  const fetchGPOfferSellerAggregates = useCallback(
    async (GPOfferID) => {
      let gpOfferSellerAggregates;
      try {
        const credentials = await Auth.currentCredentials();
        const lambdaClient = new LambdaClient({
          credentials: Auth.essentialCredentials(credentials),
          region: config.aws_project_region,
        });
        const user = await Auth.currentAuthenticatedUser({ bypassCache: true });

        const body = {
          target: "listGPOfferSellerAggregateDetails",
          access_token: user.signInUserSession.idToken.jwtToken,
          filterTerms: {
            GPOfferID: GPOfferID,
          },
          rowStart: (currentGPOfferSellerAggregatePage - 1) * pageSize,
          pageSize: pageSize,
        };

        const params = {
          FunctionName: `dataops-${await getBackendName()}`,
          Payload: JSON.stringify({ body }),
        };

        const command = new InvokeCommand(params);
        const data = await lambdaClient.send(command);
        let result = JSON.parse(new TextDecoder("utf-8").decode(data.Payload));

        if (result.statusCode === 200) {
          setGPOfferSellerAggregateItems(result.items);
          gpOfferSellerAggregates = result.items;
          setTotalGPOfferSellerAggregatePages(
            Math.ceil(result.total / pageSize)
          );
        } else {
          console.error("Error fetching gpOffers: ", result);
        }
      } catch (error) {
        console.error("Error fetching gpOffer seller aggregates: ", error);
      }

      return gpOfferSellerAggregates;
    },
    [currentGPOfferSellerAggregatePage]
  );

  // Fetch Products - Enhanced to store by ProductID for lookup
  const fetchProducts = useCallback(async () => {
    try {
      const result = await fetchData({
        target: "listProducts",
        userOrder: "ProductName",
        rowStart: 0,
        pageSize: 500,
        filterTerms: { Status: "ACTIVE" },
      });

      if (result && result.products) {
        setProducts(result.products);
      } else if (result && result.items) {
        setProducts(result.items);
      } else {
        console.error(
          "Error fetching products: Invalid response format",
          result
        );
        setProducts([]);
      }
    } catch (error) {
      console.error("Error fetching products: ", error);
      setProducts([]);
    }
  }, []);

  // Fetch Countries
  const fetchCountries = useCallback(async () => {
    try {
      const result = await fetchData({
        target: "listDropDownOptions",
        filterTerms: {
          Type: "Country",
        },
      });

      if (result && result.items) {
        setCountries(result.items);
      } else {
        console.error("Error fetching countries: Invalid response format");
        setCountries([]);
      }
    } catch (error) {
      console.error("Error fetching countries: ", error);
      setCountries([]);
    }
  }, []);

  // Handlers
  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleGPOfferSellerAggregatePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalGPOfferSellerAggregatePages) {
      setCurrentGPOfferSellerAggregatePage(newPage);
    }
  };

  const handleGPOfferRowClick = useCallback(
    async (gpOffer) => {
      setSelectedGPOffer(gpOffer);

      // Set original margin for tracking changes
      setOriginalMargin(gpOffer.GPMargin || 0);
      setIsMarginModified(false);

      // Set pricing info from the offer data
      setSellersQuantity(gpOffer.Quantity || 0);
      setSellersPrice(gpOffer.PricePerMT || 0);
      setSellersPriceForQuantity(gpOffer.TotalPrice || 0);

      // If this is an aggregated offer, fetch seller aggregate details
      if (
        gpOffer.OfferType === "Aggregated" ||
        (gpOffer.SellerItemID && gpOffer.SellerItemID.includes("Aggregated"))
      ) {
        try {
          let gpOfferSellerAggregates = await fetchGPOfferSellerAggregates(
            gpOffer.GPOfferID
          );
          if (gpOfferSellerAggregates && gpOfferSellerAggregates.length > 0) {
            let totalQuantity = 0;
            let totalPrice = 0;
            let totalQuantityPrice = 0;
            gpOfferSellerAggregates.forEach((item) => {
              totalQuantity += item.Quantity || 0;
              totalPrice += item.Price || 0;
              totalQuantityPrice += (item.Quantity || 0) * (item.Price || 0);
            });
            setSellersQuantity(totalQuantity);
            setSellersPrice(totalPrice);
            setSellersPriceForQuantity(totalQuantityPrice);
          }
        } catch (error) {
          console.error("Error fetching seller aggregates:", error);
        }
      }
    },
    [fetchGPOfferSellerAggregates]
  );

  const handleGPOfferSellerAggregateItemRowClick = (
    gpOfferSellerAggregateItem
  ) => {
    setSelectedGPOfferSellerAggregateItem(gpOfferSellerAggregateItem);
  };

  // Function to update offer data based on new GPMargin
  const updateOfferBasedOnMargin = useCallback(
    (newMargin) => {
      if (!selectedGPOffer) {
        console.warn("No offer selected to update margin");
        return;
      }

      if (newMargin === null || newMargin === undefined || newMargin === "") {
        console.warn("Invalid margin value provided");
        return;
      }

      const numericMargin = parseFloat(newMargin);
      if (isNaN(numericMargin)) {
        console.warn("Margin must be a valid number");
        return;
      }

      try {
        // Use the utility function to calculate updated pricing
        const updatedOffer = updatePricingBasedOnMargin(
          selectedGPOffer,
          numericMargin
        );

        // Update the selected offer state
        setSelectedGPOffer(updatedOffer);

        // Track if margin has been modified from original
        setIsMarginModified(originalMargin !== numericMargin);

        // Log the update for debugging
        console.log("Offer updated with new margin:", {
          originalMargin: originalMargin,
          newMargin: numericMargin,
          originalFOBPrice: selectedGPOffer.FOBSalePrice,
          newFOBPrice: updatedOffer.FOBSalePrice,
          originalCIFPrice: selectedGPOffer.CIFSalePrice,
          newCIFPrice: updatedOffer.CIFSalePrice,
          isModified: originalMargin !== numericMargin,
        });
      } catch (error) {
        console.error("Error updating offer with new margin:", error);
      }
    },
    [selectedGPOffer, originalMargin, setIsMarginModified]
  );

  // Function to save updated offer to backend (placeholder)
  const saveUpdatedOffer = useCallback(
    async (updatedOffer) => {
      try {
        setLoading(true);

        const body = {
          target: "updateGPOffer",
          offerData: {
            GPOfferID: updatedOffer.GPOfferID,
            GPMargin: updatedOffer.GPMargin,
            FOBSalePrice: updatedOffer.FOBSalePrice,
            CIFSalePrice: updatedOffer.CIFSalePrice,
            ExportDuty: updatedOffer.ExportDuty,
            ShippingInsurance: updatedOffer.ShippingInsurance,
            PricePerMT: updatedOffer.PricePerMT,
            TotalPrice: updatedOffer.TotalPrice,
            UpdatedAt: new Date().toISOString(),
          },
        };

        const result = await fetchData(body);

        if (result && result.statusCode === 200) {
          console.log("Offer updated successfully in backend");
          // Refresh the offers list
          fetchGPOffers();
        } else {
          console.error("Failed to update offer in backend:", result);
        }
      } catch (error) {
        console.error("Error saving updated offer:", error);
      } finally {
        setLoading(false);
      }
    },
    [fetchGPOffers, setLoading]
  );

  // Handler for approving an offer with confirmation
  const handleConfirmApprove = useCallback(async () => {
    // Close the confirmation modal first
    setModal((prev) => ({ ...prev, show: false }));

    if (!selectedGPOffer) {
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "No offer selected",
        autoClose: false,
        isConfirm: false,
        onConfirm: null,
      });
      return;
    }

    try {
      setLoading(true);

      const body = {
        target: "approveGPCreatedOffers",
        GPOfferID: selectedGPOffer.GPOfferID,
      };

      const result = await postQuery(body);

      if (result && result?.items) {
        setModal({
          show: true,
          type: "success",
          title: "Success",
          message: "Offer approved successfully!",
          autoClose: false,
          isConfirm: false,
          onConfirm: null,
        });
        // Refresh the offers list
        fetchGPOffers();
        // Clear selected offer
        setSelectedGPOffer(null);
      } else {
        console.error("Failed to approve offer:", result);
        setModal({
          show: true,
          type: "error",
          title: "Error",
          message: "Failed to approve offer. Please try again.",
          autoClose: false,
          isConfirm: false,
          onConfirm: null,
        });
      }
    } catch (error) {
      console.error("Error approving offer:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Error approving offer. Please try again.",
        autoClose: false,
        isConfirm: false,
        onConfirm: null,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedGPOffer, fetchGPOffers, setLoading]);

  // Handler for approve button click - shows confirmation dialog
  const handleApprove = useCallback(() => {
    setModal({
      show: true,
      type: "warning",
      title: "Confirm Approval",
      message: "Are you sure you want to approve this offer?",
      autoClose: false,
      isConfirm: true,
      onConfirm: handleConfirmApprove,
    });
  }, [handleConfirmApprove]);

  // VM Offers filter functions
  const buildVMOffersSearchParams = useCallback(
    (rowStart = 0, customFilters = vmOffersFilters) => {
      const filterTerms = {};

      if (customFilters.product) {
        filterTerms.ProductName = customFilters.product;
      }
      if (customFilters.grade) {
        filterTerms.Grade = customFilters.grade;
      }
      if (customFilters.destinationCountry) {
        filterTerms.DestinationCountry = customFilters.destinationCountry;
      }

      if (customFilters.createdDate) {
        const searchDate = new Date(customFilters.createdDate);
        const formattedDate = searchDate.toISOString().split("T")[0];
        filterTerms.CreatedAtGT = formattedDate + " 00:00:00";
        filterTerms.CreatedAtLT = formattedDate + " 23:59:59";
      }

      // if (customFilters.quantity) {
      //   const [min, max] = customFilters.quantity.split("-");
      //   if (min === "Above") {
      //     filterTerms.QuantityGT = max;
      //   } else {
      //     filterTerms.QuantityGT = min;
      //     filterTerms.QuantityLT = max;
      //   }
      // }
      if (customFilters.quantity) {
        const [minStr, maxStr] = customFilters.quantity.split("-");
        if (minStr === "Above") {
          const max = parseInt(maxStr, 10);
          filterTerms.QuantityGT = max;
        } else {
          const min = parseInt(minStr, 10);
          const max = parseInt(maxStr, 10);
          if (isNaN(min) || isNaN(max)) {
            // Optionally handle invalid numeric range, e.g., skip or log error
            return; // or continue without setting filters
          }
          if (min === max) {
            // Exact match when min and max are equal
            filterTerms.QuantityEQ = min;
          } else {
            filterTerms.QuantityGT = min - 1;
            filterTerms.QuantityLT = max + 1;
          }
        }
        delete filterTerms.searchValues;
      }
      return {
        filterTerms: filterTerms,
        userOrder: "CreatedAt DESC, OfferSequenceID DESC",
        rowStart: rowStart,
        pageSize: pageSize,
      };
    },
    [vmOffersFilters]
  );

  const hasActiveVMOffersFilters = useCallback(() => {
    return (
      vmOffersFilters.product ||
      vmOffersFilters.grade ||
      vmOffersFilters.createdDate ||
      vmOffersFilters.quantity ||
      vmOffersFilters.destinationCountry
    );
  }, [vmOffersFilters]);

  // DataTable handlers
  const handlePageChange = useCallback(
    (page) => {
      setCurrentGPOfferPage(page);
      const newRowStart = (page - 1) * pageSize;

      if (hasActiveVMOffersFilters()) {
        const searchParams = buildVMOffersSearchParams(newRowStart);
        fetchGPOffers(searchParams);
      } else {
        fetchGPOffers();
      }
    },
    [fetchGPOffers, hasActiveVMOffersFilters, buildVMOffersSearchParams]
  );

  const handleRowClick = useCallback(
    (row) => {
      handleGPOfferRowClick(row);
    },
    [handleGPOfferRowClick]
  );

  // Handle search for SearchFilter component
  const handleSearch = (searchValues) => {
    setVmOffersFilters(searchValues);
    setCurrentGPOfferPage(1);
    const searchParams = buildVMOffersSearchParams(0, searchValues);
    fetchGPOffers(searchParams);
  };

  // Effect hooks
  useEffect(() => {
    fetchGPOffers();
    fetchCountries();
    fetchProducts();
  }, [fetchGPOffers, fetchCountries, fetchProducts]);

  console.log("Selected GP Offer:", selectedGPOffer);

  return (
    <div className="compare-offer-container card">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        onCancel={handleModalClose}
        buttonText="OK"
        isConfirm={modal.isConfirm}
        onConfirm={modal.onConfirm}
        autoClose={modal.autoClose}
      />

      <div className="search-filter4">
        <div className="title4">
          <div className="head-text">Global Partner Offer</div>
        </div>
        <div className="filter4">
          <SearchFilter fields={fields} onSearch={handleSearch} />
        </div>
      </div>

      <div className="row main-row">
        <section className="offers">
          {loading && <div className="loading">Loading offers...</div>}
          {!loading && gpOffers.length === 0 && (
            <div className="empty-state">No GP Offers available</div>
          )}
          {!loading && gpOffers.length > 0 && (
            <DataTable
              columns={columns}
              data={gpOffers}
              total={totalGPOfferPages * pageSize} // Total records for pagination
              title="GP Offers Information"
              rowsPerPage={pageSize}
              onPageChange={handlePageChange}
              onRowClick={handleRowClick}
              currentPage={currentGPOfferPage}
              selectedRows={
                selectedGPOffer
                  ? [selectedGPOffer.GPOfferID || selectedGPOffer.id]
                  : []
              }
            />
          )}
        </section>
      </div>

      {selectedGPOffer && (
        <>
          <div className="row">
            <section className="product-details">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <div></div>
                <div className="actions">
                  <Button
                    variant="contained"
                    onClick={handleApprove}
                    className="actions button"
                  >
                    APPROVE
                  </Button>
                </div>
              </div>
              <div className="tabs">
                <button
                  className={activeTab === "offerInformation" ? "active" : ""}
                  onClick={() => handleTabClick("offerInformation")}
                >
                  Offer Information
                </button>
                <button
                  className={activeTab === "commodityDetails" ? "active" : ""}
                  onClick={() => handleTabClick("commodityDetails")}
                >
                  Commodity Details
                </button>
                <button
                  className={activeTab === "quantityPackaging" ? "active" : ""}
                  onClick={() => handleTabClick("quantityPackaging")}
                >
                  Quantity/Packaging
                </button>
                <button
                  className={activeTab === "deliveryLogistic" ? "active" : ""}
                  onClick={() => handleTabClick("deliveryLogistic")}
                >
                  Delivery/Logistic
                </button>
                <button
                  className={activeTab === "pricing" ? "active" : ""}
                  onClick={() => handleTabClick("pricing")}
                >
                  Pricing/Guarantee
                </button>
                <button
                  className={activeTab === "priceBreakup" ? "active" : ""}
                  onClick={() => handleTabClick("priceBreakup")}
                >
                  Price Breakup
                </button>

                <button
                  className={activeTab === "others" ? "active" : ""}
                  onClick={() => handleTabClick("others")}
                >
                  Others
                </button>
              </div>

              {(activeTab === null || activeTab === "offerInformation") && (
                <div className="tab-content active">
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="row">
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="offerSequenceID">Offer Number</label>
                          <input
                            id="offerSequenceID"
                            type="text"
                            value={selectedGPOffer?.OfferNumber || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="offerStatus">Offer Status</label>
                          <input
                            id="offerStatus"
                            type="text"
                            value={
                              selectedGPOffer?.GPOOfferStatus || "Indicative"
                            }
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="createdAt">Creation Date</label>
                          <input
                            id="createdAt"
                            type="text"
                            value={formatDateToMMDDYY(
                              selectedGPOffer?.CreatedAt
                            )}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="expires">Expiry Date</label>
                          <input
                            id="expires"
                            type="text"
                            value={formatDateToMMDDYY(selectedGPOffer?.Expires)}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="offerType">Offer Type</label>
                          <input
                            id="offerType"
                            type="text"
                            value={selectedGPOffer?.OfferType || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>

                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="approved">Approved</label>
                          <input
                            id="approved"
                            type="text"
                            value={selectedGPOffer?.Approved || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>

                        <div className="col-lg-6 col-sm-8 col-12">
                          <label htmlFor="sellerInformation">
                            Seller Information
                          </label>
                          <input
                            id="sellerInformation"
                            type="text"
                            value={selectedGPOffer?.Sellerinformation || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "commodityDetails" && (
                <div className="tab-content active">
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="row">
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="productName">Product</label>
                          <input
                            id="productName"
                            type="text"
                            value={selectedGPOffer?.ProductName || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="hsCode">HS Code</label>
                          <input
                            id="hsCode"
                            type="text"
                            value={selectedGPOffer?.HsCode || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="grade">Grade / Quality</label>
                          <input
                            id="grade"
                            type="text"
                            value={selectedGPOffer?.Grade || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="variety">Variety</label>
                          <input
                            id="variety"
                            type="text"
                            value={selectedGPOffer?.Variety || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="originCountry">Origin Country</label>
                          <input
                            id="originCountry"
                            type="text"
                            value={selectedGPOffer?.OriginCountry || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="destinationCountry">
                            Destination Country
                          </label>
                          <input
                            id="destinationCountry"
                            type="text"
                            value={selectedGPOffer?.DestinationCountry || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-6 col-sm-8 col-12">
                          <label htmlFor="productSpecifications">
                            Product Specifications
                          </label>
                          <textarea
                            id="productSpecifications"
                            className="form-control"
                            rows="4"
                            value={selectedGPOffer?.ProductSpecifications || ""}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "quantityPackaging" && (
                <div className="tab-content active">
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="row">
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="quantity">Quantity (MT)</label>
                          <input
                            id="quantity"
                            type="text"
                            value={formatData(selectedGPOffer?.Quantity)}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="packagingType">Packaging Type</label>
                          <input
                            id="packagingType"
                            type="text"
                            value={selectedGPOffer?.PackagingType || "Bags"}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="packagingSize">Packaging Size</label>
                          <input
                            id="packagingSize"
                            type="text"
                            value={selectedGPOffer?.PackagingSize || "50kg"}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-9 col-sm-8 col-12">
                          <label htmlFor="additionalPackagingNotes">
                            Additional Packaging Notes
                          </label>
                          <textarea
                            id="additionalPackagingNotes"
                            value={
                              selectedGPOffer?.AdditionalPackagingNotes || ""
                            }
                            className="form-control"
                            readOnly
                            rows="3"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "deliveryLogistic" && (
                <div className="tab-content active">
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="row">
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="shippingMethod">
                            Shipping Method
                          </label>
                          <input
                            id="shippingMethod"
                            type="text"
                            value={
                              selectedGPOffer?.ShippingMethodPreference || ""
                            }
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="portOfLoading">Port of Loading</label>
                          <input
                            id="portOfLoading"
                            type="text"
                            value={selectedGPOffer?.LoadingPort || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="portOfDestination">
                            Port of Destination
                          </label>
                          <input
                            id="portOfDestination"
                            type="text"
                            value={selectedGPOffer?.DischargePort || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>

                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="portOfDestination">Incoterms</label>
                          <input
                            id="Incoterms"
                            type="text"
                            value={selectedGPOffer?.INCO || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="TransitTime">Transit Time</label>
                          <input
                            id="TransitTime"
                            type="text"
                            value={selectedGPOffer?.TransitTime || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="PartialDeliveryAllowed">
                            Partial Delivery Allowed?
                          </label>
                          <input
                            id="PartialDeliveryAllowed"
                            type="text"
                            value={
                              selectedGPOffer?.PartialDeliveryAllowed
                                ? "Yes"
                                : "No"
                            }
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="DailyUnloadingCapacity">
                            Daily Unloading Capacity
                          </label>
                          <input
                            id="DailyUnloadingCapacity"
                            type="text"
                            value={
                              selectedGPOffer?.DailyUnloadingCapacity
                                ? "Yes"
                                : "No"
                            }
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="UnloadingResponsibility">
                            Unloading Responsibility
                          </label>
                          <input
                            id="UnloadingResponsibility"
                            type="text"
                            value={
                              selectedGPOffer?.UnloadingResponsibility || ""
                            }
                            className="form-control"
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "pricing" && (
                <div className="tab-content active">
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="row">
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="fobSalePrice">
                            FOB Sale Price - Total (INR)
                          </label>
                          <input
                            id="fobSalePrice"
                            type="text"
                            value={formatData(
                              (selectedGPOffer?.BasePrice || 0) +
                              (selectedGPOffer?.GPMargin || 0)
                            )}
                            className="form-control"
                            readOnly
                          />
                        </div>

                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="fobSalePrice">
                            FOB Sale Price - Total (USD)
                          </label>
                          <input
                            id="fobSalePrice"
                            type="text"
                            value={formatData(
                              ((selectedGPOffer?.BasePrice || 0) +
                                (selectedGPOffer?.GPMargin || 0)) *
                              INRToUsd
                            )}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="fobSalePrice">
                            FOB Sale Price (INR/MT)
                          </label>
                          <input
                            id="fobSalePrice"
                            type="text"
                            value={formatData(
                              ((selectedGPOffer?.BasePrice || 0) +
                                (selectedGPOffer?.GPMargin || 0)) /
                              (selectedGPOffer?.Quantity || 1)
                            )}
                            className="form-control"
                            readOnly
                          />
                        </div>

                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="fobSalePrice">
                            FOB Sale Price (USD/MT)
                          </label>
                          <input
                            id="fobSalePrice"
                            type="text"
                            value={formatData(
                              (((selectedGPOffer?.BasePrice || 0) +
                                (selectedGPOffer?.GPMargin || 0)) /
                                (selectedGPOffer?.Quantity || 1)) *
                              INRToUsd
                            )}
                            className="form-control"
                            readOnly
                          />
                        </div>

                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="cifSalePrice">
                            CIF Sale Price - Total (INR)
                          </label>
                          <input
                            id="cifSalePrice"
                            type="text"
                            value={formatData(selectedGPOffer?.CIFSalePrice)}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="cifSalePrice">
                            CIF Sale Price - Total (USD)
                          </label>
                          <input
                            id="cifSalePrice"
                            type="text"
                            value={formatData(
                              selectedGPOffer?.CIFSalePrice * INRToUsd
                            )}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="cifSalePrice">
                            CIF Sale Price (INR/MT)
                          </label>
                          <input
                            id="cifSalePrice"
                            type="text"
                            value={formatData(
                              selectedGPOffer?.CIFSalePrice /
                              selectedGPOffer?.Quantity
                            )}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="cifSalePrice">
                            CIF Sale Price (USD/MT)
                          </label>
                          <input
                            id="cifSalePrice"
                            type="text"
                            value={formatData(
                              (selectedGPOffer?.CIFSalePrice /
                                selectedGPOffer?.Quantity) *
                              INRToUsd
                            )}
                            className="form-control"
                            readOnly
                          />
                        </div>

                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="paymentTerms">Payment Terms</label>
                          <input
                            id="paymentTerms"
                            type="text"
                            value={selectedGPOffer?.PaymentTerms || ""}
                            className="form-control"
                            readOnly
                          />
                        </div>
                        <div className="col-lg-3 col-sm-4 col-6">
                          <label htmlFor="paymentMethod">Payment Method</label>
                          <input
                            id="paymentMethod"
                            type="text"
                            value={
                              selectedGPOffer?.PreferredPaymentMethod || ""
                            }
                            className="form-control"
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "priceBreakup" && (
                <div className="tab-content active">
                  <div className="row">
                    <div className="col-lg-12">
                      <table className="datatable">
                        <tr className="dataheader">
                          <th colSpan="1">Pricing Item</th>
                          <th rowSpan="1">Pricing Component</th>
                          <th rowSpan="1">Amount (Rs)</th>
                          <th rowSpan="1">Amount (USD)</th>
                        </tr>
                        <tr className="primerow">
                          <td>Seller's Quantity (MT)</td>
                          <td className="currency right">
                            {formatData(sellersQuantity)}
                          </td>
                          <td></td>
                          <td></td>
                        </tr>
                        <tr className="altrow">
                          <td>Seller's Price /MT (Rs.)</td>
                          <td className="currency right">
                            {formatData(sellersPrice)}
                          </td>
                          <td></td>
                          <td></td>
                        </tr>
                        <tr className="primerow">
                          <td>Seller's Total Price</td>
                          <td></td>
                          <td className="currency right">
                            {formatData(sellersPriceForQuantity)}
                          </td>
                          <td className="currency right">
                            {formatData(sellersPriceForQuantity * INRToUsd)}
                          </td>
                        </tr>
                        <tr className="altrow">
                          <td>AgroPreneur's Charges</td>
                          <td className="currency right">
                            {formatData(
                              selectedGPOffer?.APCommissionPercentage
                            )}
                            %
                          </td>
                          <td className="currency right">
                            {formatData(selectedGPOffer?.APCommission)}
                          </td>
                          <td className="currency right">
                            {formatData(
                              selectedGPOffer?.APCommission * INRToUsd
                            )}
                          </td>
                        </tr>
                        <tr className="primerow">
                          <td>Sorting & Grading Charges</td>
                          <td className="currency right">
                            {formatData(
                              selectedGPOffer?.SortGradeChargedPercentage
                            )}
                            %
                          </td>
                          <td className="currency right">
                            {formatData(selectedGPOffer?.SortGradeCharged)}
                          </td>
                          <td className="currency right">
                            {formatData(
                              selectedGPOffer?.SortGradeCharged * INRToUsd
                            )}
                          </td>
                        </tr>
                        <tr className="altrow">
                          <td>Operations & Finance Charges</td>
                          <td className="currency right">
                            {formatData(
                              selectedGPOffer?.OpsFinChargesPercentage
                            )}
                            %
                          </td>
                          <td className="currency right">
                            {formatData(selectedGPOffer?.OpsFinCharges)}
                          </td>
                          <td className="currency right">
                            {formatData(
                              selectedGPOffer?.OpsFinCharges * INRToUsd
                            )}
                          </td>
                        </tr>
                        <tr className="altrow">
                          <td>Inland Transport Charges</td>
                          <td className="currency right">
                            {selectedGPOffer?.InlandTransport
                              ? formatData(
                                selectedGPOffer?.InlandTransport /
                                sellersQuantity
                              )
                              : 0}
                          </td>
                          <td className="currency right">
                            {formatData(selectedGPOffer?.InlandTransport)}
                          </td>
                          <td className="currency right">
                            {formatData(
                              selectedGPOffer?.InlandTransport * INRToUsd
                            )}
                          </td>
                        </tr>
                        <tr className="primerow bold-font">
                          <td>Seller's BASE Price</td>
                          <td></td>
                          <td className="currency right">
                            {formatData(selectedGPOffer?.BasePrice)}
                          </td>
                          <td className="currency right">
                            {formatData(selectedGPOffer?.BasePrice * INRToUsd)}
                          </td>
                        </tr>
                        <tr className="altrow">
                          <td>Margin</td>
                          <td className="currency right">
                            {formatData(
                              (selectedGPOffer?.GPMargin * 100) /
                              selectedGPOffer.BasePrice
                            )}
                            %
                          </td>
                          <td className="currency right">
                            {formatData(selectedGPOffer?.GPMargin)}
                          </td>
                          <td className="currency right">
                            {formatData(selectedGPOffer?.GPMargin * INRToUsd)}
                          </td>
                        </tr>
                        <tr className="primerow bold-font">
                          <td>FOB Sale Price</td>
                          <td></td>
                          <td className="currency right">
                            {formatData(
                              (selectedGPOffer?.BasePrice || 0) +
                              (selectedGPOffer?.GPMargin || 0)
                            )}
                          </td>
                          <td className="currency right">
                            {formatData(
                              ((selectedGPOffer?.BasePrice || 0) +
                                (selectedGPOffer?.GPMargin || 0)) *
                              INRToUsd
                            )}
                          </td>
                        </tr>
                        <tr className="altrow">
                          <td>Export Duty</td>
                          <td className="currency right">
                            {formatData(selectedGPOffer?.ExportDutyPercentage)}%
                          </td>
                          <td className="currency right">
                            {formatData(selectedGPOffer?.ExportDuty)}
                          </td>
                          <td className="currency right">
                            {formatData(selectedGPOffer?.ExportDuty * INRToUsd)}
                          </td>
                        </tr>
                        <tr className="altrow">
                          <td>Shipping & Insurance</td>
                          <td className="currency right">
                            Rs.{" "}
                            {formatData(
                              selectedGPOffer?.ShippingInsurancePercentage
                            )}{" "}
                            / MT
                          </td>
                          <td className="currency right">
                            {formatData(selectedGPOffer?.ShippingInsurance)}
                          </td>
                          <td className="currency right">
                            {formatData(
                              selectedGPOffer?.ShippingInsurance * INRToUsd
                            )}
                          </td>
                        </tr>
                        <tr className="altrow">
                          <td>GST (Local Taxes)</td>
                          <td></td>
                          <td className="currency right">
                            {formatData(selectedGPOffer?.GST)}
                          </td>
                          <td className="currency right">
                            {formatData(selectedGPOffer?.GST * INRToUsd)}
                          </td>
                        </tr>
                        <tr className="primerow bold-font">
                          <td>CIF Sale Price</td>
                          <td></td>
                          <td className="currency right">
                            {formatData(
                              (selectedGPOffer?.BasePrice || 0) +
                              (selectedGPOffer?.GPMargin || 0) +
                              (selectedGPOffer?.ShippingInsurance || 0) +
                              (selectedGPOffer?.GST || 0) +
                              (selectedGPOffer?.ExportDuty || 0)
                            )}
                          </td>
                          <td className="currency right">
                            {formatData(
                              ((selectedGPOffer?.BasePrice || 0) +
                                (selectedGPOffer?.GPMargin || 0) +
                                (selectedGPOffer?.ShippingInsurance || 0) +
                                (selectedGPOffer?.GST || 0) +
                                (selectedGPOffer?.ExportDuty || 0)) *
                              INRToUsd
                            )}
                          </td>
                        </tr>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "others" && (
                <div className="tab-content active">
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="row">
                        <div className="col-lg-12">
                          <label htmlFor="additionalNotes">
                            Special Instruction for buyer
                          </label>
                          <textarea
                            id="additionalNotes"
                            value={
                              selectedGPOffer?.SpecialInstructionsBuyer || ""
                            }
                            className="form-control"
                            readOnly
                            rows="5"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>

          {(activeTab === "offerInformation" || activeTab === "priceBreakup") &&
            selectedGPOffer &&
            (selectedGPOffer.OfferType === "Aggregated" ||
              (selectedGPOffer.SellerItemID &&
                selectedGPOffer.SellerItemID.includes("Aggregated"))) && (
              <div className="row">
                <label htmlFor="firstName" className="offers">
                  Seller Aggregate Details for Offer:{" "}
                  {selectedGPOffer.OfferSequenceID || "N/A"}
                </label>
                <section className="offers">
                  <table>
                    <thead>
                      <tr>
                        <th>Seller Type</th>
                        <th>Seller Name</th>
                        <th>State</th>
                        <th>Quantity (MT)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gpOfferSellerAggregateItems &&
                        gpOfferSellerAggregateItems.map(
                          (gpOfferSellerAggregateItem) => (
                            <tr
                              key={gpOfferSellerAggregateItem.SellerItemID}
                              onClick={() =>
                                handleGPOfferSellerAggregateItemRowClick(
                                  gpOfferSellerAggregateItem
                                )
                              }
                              className={
                                selectedGPOfferSellerAggregateItem &&
                                  selectedGPOfferSellerAggregateItem?.SellerItemID ===
                                  gpOfferSellerAggregateItem.SellerItemID
                                  ? "selected"
                                  : ""
                              }
                            >
                              <td>{gpOfferSellerAggregateItem.SellerType}</td>
                              <td>{gpOfferSellerAggregateItem.SellerName}</td>
                              <td>{gpOfferSellerAggregateItem.State}</td>
                              <td>{gpOfferSellerAggregateItem.Quantity}</td>
                            </tr>
                          )
                        )}
                      {gpOfferSellerAggregateItems.length === 0 && (
                        <tr>
                          <td colSpan="4">No aggregate items available</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className="pagination">
                    <button
                      onClick={() =>
                        handleGPOfferSellerAggregatePageChange(
                          currentGPOfferSellerAggregatePage - 1
                        )
                      }
                      disabled={currentGPOfferSellerAggregatePage === 1}
                    >
                      {"<"}
                    </button>
                    {Array.from(
                      { length: totalGPOfferSellerAggregatePages },
                      (_, index) => (
                        <button
                          key={index + 1}
                          onClick={() =>
                            handleGPOfferSellerAggregatePageChange(index + 1)
                          }
                          className={
                            currentGPOfferSellerAggregatePage === index + 1
                              ? "active"
                              : ""
                          }
                        >
                          {index + 1}
                        </button>
                      )
                    )}
                    <button
                      onClick={() =>
                        handleGPOfferSellerAggregatePageChange(
                          currentGPOfferSellerAggregatePage + 1
                        )
                      }
                      disabled={
                        currentGPOfferSellerAggregatePage ===
                        totalGPOfferSellerAggregatePages
                      }
                    >
                      {">"}
                    </button>
                  </div>
                </section>
              </div>
            )}
        </>
      )}
    </div>
  );
};

export default GTPOffer;
